import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { playUrgentSound, playErrorSound, playSuggestionSound } from '../lib/sounds';
import { toast } from 'sonner';
import {
  Mic, MicOff, Upload, Play, Square, Loader2, Phone,
  XCircle, Lightbulb, Info, ChevronRight, Bell, User, Users,
  ArrowRightLeft, Volume2, Zap, Radio, ThumbsUp, ThumbsDown,
  PhoneCall, PhoneOff
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DialPad from '../components/DialPad';

const SUGGESTION_CONFIG = {
  error_dato: { icon: XCircle, color: '#ef4444', label: 'Error de Datos', borderClass: 'border-l-4 border-l-red-500' },
  oportunidad: { icon: Lightbulb, color: '#f59e0b', label: 'Oportunidad', borderClass: 'border-l-4 border-l-amber-500' },
  consejo: { icon: Info, color: '#3b82f6', label: 'Consejo', borderClass: 'border-l-4 border-l-blue-500' },
};

const URGENCY_COLORS = {
  high: { bg: 'rgba(239,68,68,0.15)', text: '#ef4444' },
  medium: { bg: 'rgba(245,158,11,0.15)', text: '#f59e0b' },
  low: { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
};

const SPEAKER_COLORS = {
  'Agente': { bg: 'var(--accent-primary)', text: 'white', bubble: 'transparent', border: 'var(--border-color)' },
  'Cliente': { bg: 'var(--surface-secondary)', text: 'var(--text-muted)', bubble: 'var(--surface-secondary)', border: 'transparent' },
};

const AVAILABLE_SPEAKERS = ['Agente', 'Cliente', 'Supervisor', 'Tecnico'];

export default function CallPage() {
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [deepgramConnected, setDeepgramConnected] = useState(false);
  const [newSuggestionCount, setNewSuggestionCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [interimSpeaker, setInterimSpeaker] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [twilioLiveSession, setTwilioLiveSession] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakerMap, setSpeakerMap] = useState({ 0: 'Agente', 1: 'Cliente' });
  const [editingSpeakerIdx, setEditingSpeakerIdx] = useState(null);
  const wsRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const suggestionsEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const streamRef = useRef(null);
  const audioProcessorRef = useRef(null);
  const deepgramReadyRef = useRef(false);
  const sessionRef = useRef(null);
  const isStreamingRef = useRef(false);
  const reconnectTimerRef = useRef(null);

  // Twilio calling state
  const [showDialPad, setShowDialPad] = useState(false);
  const [twilioCall, setTwilioCall] = useState(null);
  const [twilioDevice, setTwilioDevice] = useState(null);
  const [twilioReady, setTwilioReady] = useState(false);
  const [deviceHealthy, setDeviceHealthy] = useState(true);
  const [callingNumber, setCallingNumber] = useState('');
  const [callStatus, setCallStatus] = useState(''); // '', 'connecting', 'ringing', 'in-progress', 'ended'
  const [callerNumber, setCallerNumber] = useState('');
  const twilioDeviceRef = useRef(null);
  const twilioCallRef = useRef(null);

  const scrollTranscript = useCallback(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const scrollSuggestions = useCallback(() => {
    suggestionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollTranscript(); }, [transcript, scrollTranscript]);
  useEffect(() => { scrollSuggestions(); }, [suggestions, scrollSuggestions]);

  // Close speaker dropdown on outside click
  useEffect(() => {
    if (editingSpeakerIdx === null) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid^="speaker-dropdown-"]') && !e.target.closest('[data-testid^="speaker-label-"]')) {
        setEditingSpeakerIdx(null);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [editingSpeakerIdx]);

  useEffect(() => {
    return () => {
      sessionRef.current = null;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      stopMicrophone();
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
      if (twilioDeviceRef.current) {
        twilioDeviceRef.current.destroy();
        twilioDeviceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // =============================================
  // TWILIO BROWSER CALLING
  // =============================================
  const initTwilioDevice = async () => {
    try {
      const { Device } = await import('@twilio/voice-sdk');
      const data = await api.post('/api/twilio/access-token');
      if (!data.token) throw new Error('No se recibio token');

      setCallerNumber(data.phone_number || '');
      const device = new Device(data.token, {
        logLevel: 1,
      });

      device.on('registered', () => {
        setTwilioReady(true);
        setDeviceHealthy(true);
        logger('twilio.device.registered');
      });
      device.on('error', (err) => {
        logger('twilio.device.error', err.message);
        setDeviceHealthy(false);
        toast.error(`Error Twilio: ${err.message}`);
      });
      device.on('tokenWillExpire', async () => {
        try {
          const refreshed = await api.post('/api/twilio/access-token');
          device.updateToken(refreshed.token);
        } catch (e) { /* ignore */ }
      });

      await device.register();
      setTwilioDevice(device);
      twilioDeviceRef.current = device;
      return device;
    } catch (err) {
      toast.error(err.message || 'Error al inicializar Twilio');
      return null;
    }
  };

  const makeCall = async (toNumber) => {
    try {
      // Ensure E.164 format
      let formattedNumber = toNumber.replace(/[\s\-()]/g, '');
      if (!formattedNumber.startsWith('+')) {
        formattedNumber = `+${formattedNumber}`;
      }

      setCallingNumber(formattedNumber);
      setCallStatus('connecting');
      setTwilioLiveSession(true);

      // Create session first
      const sessionData = await api.post('/api/sessions', {
        source: 'twilio_outbound',
        agent_name: 'Agente',
      });
      setSession(sessionData);
      sessionRef.current = sessionData;
      setTranscript([]);
      setSuggestions([]);

      // Connect WebSocket for this session (viewer mode)
      connectWebSocket(sessionData.id);

      // Init Twilio device if needed
      let device = twilioDeviceRef.current;
      if (!device || !deviceHealthy) {
        if (device) {
          try { device.destroy(); } catch (_) {}
          twilioDeviceRef.current = null;
          setTwilioDevice(null);
        }
        device = await initTwilioDevice();
        if (!device) {
          setCallStatus('');
          try { await api.put(`/api/sessions/${sessionData.id}/end`); } catch (_) {}
          setSession(null);
          return;
        }
      }

      // Make the call
      const call = await device.connect({
        params: { To: formattedNumber, SessionId: sessionData.id },
      });
      setTwilioCall(call);
      twilioCallRef.current = call;
      setShowDialPad(false);

      call.on('accept', () => {
        setCallStatus('in-progress');
        toast.success(`Llamada conectada: ${toNumber}`);
      });
      call.on('ringing', () => {
        setCallStatus('ringing');
      });
      call.on('disconnect', () => {
        setCallStatus('ended');
        setTwilioCall(null);
        twilioCallRef.current = null;
        toast.info('Llamada finalizada');
      });
      call.on('cancel', () => {
        setCallStatus('');
        setTwilioCall(null);
        twilioCallRef.current = null;
      });
      call.on('error', (err) => {
        toast.error(`Error en llamada: ${err.message}`);
        setCallStatus('');
        setTwilioCall(null);
        twilioCallRef.current = null;
        setDeviceHealthy(false);
      });
    } catch (err) {
      toast.error('Error al realizar llamada: ' + (err.message || ''));
      setCallStatus('');
    }
  };

  const hangupCall = () => {
    try {
      if (twilioCallRef.current) {
        twilioCallRef.current.disconnect();
      } else if (twilioDeviceRef.current) {
        twilioDeviceRef.current.disconnectAll();
      }
    } catch (_) {}
    setCallStatus('ended');
    setTwilioCall(null);
    twilioCallRef.current = null;
  };

  const logger = (...args) => console.log('[TwilioDevice]', ...args);

  // =============================================
  // MICROPHONE + DEEPGRAM STREAMING
  // =============================================
  const startMicrophone = async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error('No hay conexión WebSocket. Inicia una sesión primero.');
      return;
    }

    try {
      // Request mic access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      streamRef.current = stream;
      setupAudioLevel(stream);

      // Tell backend to start Deepgram connection
      // MediaRecorder will start AFTER deepgram_connected status is received
      wsRef.current.send(JSON.stringify({ type: 'start_deepgram' }));

      setIsStreaming(true);
      isStreamingRef.current = true;
      toast.success('Micrófono activado — conectando Deepgram...');

    } catch (err) {
      console.error('Microphone error:', err);
      if (err.name === 'NotAllowedError') {
        toast.error('Permiso de micrófono denegado. Habilítalo en la configuración del navegador.');
      } else {
        toast.error('Error al acceder al micrófono: ' + err.message);
      }
    }
  };

  // Start/Restart MediaRecorder — called when Deepgram confirms it's ready
  const startMediaRecorder = () => {
    // Stop any existing recorder first (important for reconnections)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch(e) { /* ok */ }
      mediaRecorderRef.current = null;
    }

    if (!streamRef.current || !streamRef.current.active) {
      console.warn('No active audio stream for MediaRecorder');
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/ogg;codecs=opus';

    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0 && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(event.data);
      }
    };

    // Start fresh — first chunk will contain the WebM header
    recorder.start(250);
    console.log('MediaRecorder started (fresh WebM stream with header)');
  };

  const stopMicrophone = () => {
    // Stop MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // Stop audio stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Stop audio level monitoring
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current = null;
    }

    // Tell backend to stop Deepgram
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop_deepgram' }));
    }

    setIsStreaming(false);
    isStreamingRef.current = false;
    setInterimText('');
    setInterimSpeaker('');
    setAudioLevel(0);
    setIsSpeaking(false);
    setDeepgramConnected(false);
    deepgramReadyRef.current = false;
  };

  const setupAudioLevel = (stream) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(100, (avg / 128) * 100));
        animFrameRef.current = requestAnimationFrame(updateLevel);
      };
      updateLevel();
    } catch (e) { /* optional */ }
  };

  // =============================================
  // SESSION MANAGEMENT
  // =============================================
  const startSession = async () => {
    setLoading(true);
    try {
      const data = await api.post('/api/sessions', { agent_name: 'Agente' });
      setSession(data);
      sessionRef.current = data;
      setTranscript([]);
      setSuggestions([]);
      setNewSuggestionCount(0);
      setSpeakerMap({ 0: 'Agente', 1: 'Cliente' });
      connectWebSocket(data.id);
      toast.success('Sesión iniciada');
    } catch (err) {
      toast.error('Error al crear sesión: ' + err.message);
    }
    setLoading(false);
  };

  const connectWebSocket = (sessionId, isReconnect = false) => {
    // Clear any pending reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const wsUrl = api.getWsUrl(`/api/ws/session/${sessionId}`);
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      if (isReconnect) {
        console.log('WebSocket reconnected');
        // If we were streaming audio, restart the pipeline
        if (isStreamingRef.current) {
          // CRITICAL: Stop old MediaRecorder FIRST to prevent stale headerless
          // WebM chunks from being sent to the new Deepgram connection
          if (mediaRecorderRef.current) {
            mediaRecorderRef.current.ondataavailable = null; // Kill handler before stop
            if (mediaRecorderRef.current.state !== 'inactive') {
              try { mediaRecorderRef.current.stop(); } catch(e) { /* ok */ }
            }
            mediaRecorderRef.current = null;
          }
          // Small delay to ensure old Deepgram connection is fully closed on backend
          setTimeout(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'start_deepgram' }));
            }
          }, 500);
        }
      } else {
        toast.success('Conectado al servidor');
      }
    };

    ws.onmessage = (event) => {
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          // Handle keepalive pings from server
          if (msg.type === 'ping') {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'pong' }));
            }
            return;
          }
          if (msg.type === 'pong') return;
          handleWsMessage(msg);
        } catch (e) { /* ignore parse errors */ }
      }
    };

    ws.onclose = (e) => {
      setIsConnected(false);
      setDeepgramConnected(false);
      deepgramReadyRef.current = false;
      console.log('WebSocket closed:', e.code, e.reason);
      
      // Auto-reconnect if session is active
      if (sessionRef.current) {
        console.log('Scheduling auto-reconnect in 1s...');
        reconnectTimerRef.current = setTimeout(() => {
          if (sessionRef.current) {
            connectWebSocket(sessionRef.current.id, true);
          }
        }, 1000);
      }
    };
    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
    };
  };

  const handleWsMessage = (msg) => {
    switch (msg.type) {
      case 'transcript':
        // Final transcript from Deepgram (via backend)
        setTranscript(prev => [...prev, msg.data]);
        setInterimText('');
        setInterimSpeaker('');
        break;

      case 'transcript_interim':
        // Interim result from Deepgram
        setInterimText(msg.data.text);
        setInterimSpeaker(msg.data.speaker || '');
        break;

      case 'utterance_end':
        setInterimText('');
        setInterimSpeaker('');
        break;

      case 'speech_started':
        setIsSpeaking(true);
        setTimeout(() => setIsSpeaking(false), 2000);
        break;

      case 'suggestion':
        setSuggestions(prev => [...prev, msg.data]);
        setNewSuggestionCount(prev => prev + 1);
        setTimeout(() => setNewSuggestionCount(prev => Math.max(0, prev - 1)), 5000);
        // Play notification sound based on type and urgency
        if (msg.data.urgency === 'high' || msg.data.type === 'error_dato') {
          if (msg.data.type === 'error_dato') {
            playErrorSound();
          } else {
            playUrgentSound();
          }
        } else {
          playSuggestionSound();
        }
        break;

      case 'status':
        if (msg.status === 'deepgram_connected' || msg.status === 'twilio_viewer_connected') {
          setDeepgramConnected(true);
          deepgramReadyRef.current = true;
          if (msg.status === 'deepgram_connected') {
            startMediaRecorder();
            toast.success('Deepgram conectado — detección automática de hablantes activa');
          }
        } else if (msg.status === 'deepgram_disconnected') {
          setDeepgramConnected(false);
          deepgramReadyRef.current = false;
        } else if (msg.status === 'completed') {
          stopMicrophone();
          toast.info(msg.message);
        } else if (msg.status === 'twilio_call_ended') {
          setTwilioLiveSession(false);
          setDeepgramConnected(false);
        }
        break;

      case 'session_source':
        if (msg.source === 'twilio_live' || msg.source === 'twilio_outbound') {
          setTwilioLiveSession(true);
        }
        break;

      case 'speaker_map_updated':
        setSpeakerMap(prev => ({ ...prev, ...msg.map }));
        break;

      case 'error':
        toast.error(msg.message);
        break;

      default:
        break;
    }
  };

  const toggleStreaming = () => {
    if (isStreaming) {
      stopMicrophone();
      toast.info('Micrófono desactivado');
    } else {
      startMicrophone();
    }
  };

  const endSession = async () => {
    // Stop auto-reconnect
    sessionRef.current = null;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    stopMicrophone();
    hangupCall();
    if (wsRef.current) wsRef.current.close();
    if (session) {
      try {
        await api.put(`/api/sessions/${session.id}/end`);
        toast.success('Sesión finalizada');
      } catch (err) {
        toast.error('Error al finalizar sesión');
      }
    }
    setIsConnected(false);
    setTwilioLiveSession(false);
    setSession(null);
    setCallStatus('');
    setCallingNumber('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !session) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.upload(`/api/sessions/${session.id}/upload-audio`, formData);
      toast.success(`Archivo "${file.name}" subido. Procesando con Deepgram...`);
      startPolling(session.id);
    } catch (err) {
      toast.error('Error al subir archivo: ' + err.message);
    }
  };

  const startPolling = (sessionId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const data = await api.get(`/api/sessions/${sessionId}`);
        setTranscript(data.transcript_full || []);
        setSuggestions(data.suggestions || []);
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          toast.info('Procesamiento completado');
        }
      } catch (err) { /* ignore */ }
    }, 2000);
  };

  const requestAnalysis = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_analysis' }));
      toast.info('Solicitando análisis IA...');
    }
  };

  const sendFeedback = async (suggestionId, feedback) => {
    try {
      await api.put(`/api/suggestions/${suggestionId}/feedback`, { feedback });
      setSuggestions(prev => prev.map(s =>
        s.id === suggestionId ? { ...s, feedback } : s
      ));
    } catch (err) {
      /* ignore */
    }
  };

  const swapSpeakers = () => {
    const newMap = { 0: speakerMap[1] || 'Cliente', 1: speakerMap[0] || 'Agente' };
    setSpeakerMap(newMap);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'set_speaker_map', map: newMap }));
    }
    toast.info(`Hablantes intercambiados: Hablante 0 → ${newMap[0]}, Hablante 1 → ${newMap[1]}`);
  };

  const changeSpeakerForEntry = async (idx, newSpeaker) => {
    if (!session) return;
    try {
      await api.put(`/api/sessions/${session.id}/transcript/${idx}/speaker`, { speaker: newSpeaker });
      setTranscript(prev => prev.map((e, i) => i === idx ? { ...e, speaker: newSpeaker } : e));
      setEditingSpeakerIdx(null);
    } catch (err) {
      toast.error('Error al cambiar hablante');
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full" data-testid="call-page">
      {/* LEFT: Transcription Area (60%) */}
      <div className="w-full lg:w-[60%] flex flex-col border-r" style={{ borderColor: 'var(--border-color)' }}>
        {/* Top Bar */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b flex-wrap gap-2"
          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            {!session ? (
              <>
                <button
                  onClick={startSession}
                  disabled={loading}
                  data-testid="start-session-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg"
                  style={{ backgroundColor: 'var(--accent-primary)' }}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => setShowDialPad(true)}
                  data-testid="open-dialer-btn"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg"
                  style={{ backgroundColor: '#22c55e' }}
                >
                  <PhoneCall size={16} />
                  Realizar Llamada
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={toggleStreaming}
                  data-testid="mic-toggle-btn"
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isStreaming ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : ''
                  }`}
                  style={!isStreaming ? { backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' } : {}}
                >
                  {isStreaming ? <MicOff size={16} /> : <Mic size={16} />}
                  {isStreaming ? 'Detener' : 'Micrófono'}
                </button>

                {isStreaming && (
                  <div className="flex items-center gap-2" data-testid="audio-level-indicator">
                    <Volume2 size={14} style={{ color: audioLevel > 20 ? '#22c55e' : 'var(--text-muted)' }} />
                    <div className="w-16 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-75"
                        style={{
                          width: `${audioLevel}%`,
                          backgroundColor: audioLevel > 60 ? '#22c55e' : audioLevel > 30 ? '#f59e0b' : 'var(--text-muted)',
                        }}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-audio-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}
                >
                  <Upload size={16} />
                  Audio
                </button>
                <input ref={fileInputRef} type="file" accept=".mp3,.wav,.m4a,.ogg" className="hidden" onChange={handleFileUpload} data-testid="audio-file-input" />

                <button
                  onClick={requestAnalysis}
                  data-testid="request-analysis-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-secondary)' }}
                >
                  <Zap size={16} />
                  Analizar
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {session && (
              <>
                {/* Speaker swap */}
                <button
                  onClick={swapSpeakers}
                  data-testid="swap-speakers-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-muted)' }}
                  title="Intercambiar hablantes"
                >
                  <ArrowRightLeft size={13} />
                  <span className="hidden sm:inline">Intercambiar</span>
                </button>

                {/* Status */}
                <div className="flex items-center gap-2 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                  <span className={`w-2 h-2 rounded-full ${
                    callStatus === 'in-progress' ? 'bg-green-500 pulse-dot' :
                    callStatus === 'ringing' ? 'bg-amber-500 pulse-dot' :
                    callStatus === 'connecting' ? 'bg-blue-500 pulse-dot' :
                    (isStreaming || twilioLiveSession) && deepgramConnected ? 'bg-red-500 pulse-dot' :
                    isConnected ? 'bg-green-500 pulse-dot' : 'bg-gray-500'
                  }`} />
                  {callStatus === 'in-progress' ? `En llamada: ${callingNumber}` :
                   callStatus === 'ringing' ? `Llamando: ${callingNumber}` :
                   callStatus === 'connecting' ? 'Conectando...' :
                    (isStreaming || twilioLiveSession) && deepgramConnected ? 'Escuchando' :
                   isConnected ? 'Conectado' : 'Desconectado'}
                </div>

                {callStatus && callStatus !== 'ended' && (
                  <button
                    onClick={hangupCall}
                    data-testid="hangup-call-btn"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500 text-white transition-all duration-200 hover:bg-red-600"
                  >
                    <PhoneOff size={14} />
                    Colgar
                  </button>
                )}

                <button
                  onClick={endSession}
                  data-testid="end-session-btn"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-red-400 transition-all duration-200 hover:bg-red-500/20"
                >
                  <Square size={14} />
                  Finalizar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Hands-free mode banner */}
        {session && (isStreaming || twilioLiveSession) && deepgramConnected && (
          <div
            className="flex items-center justify-center gap-3 py-2 text-xs"
            style={{ backgroundColor: 'rgba(99,102,241,0.1)', color: 'var(--accent-primary)' }}
          >
            <Radio size={12} className={isSpeaking ? 'pulse-dot' : ''} />
            <span className="font-semibold">Modo Manos Libres</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ color: 'var(--text-muted)' }}>
              Deepgram detecta automáticamente quién habla
            </span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span>
              <span className="font-mono" style={{ color: 'var(--accent-primary)' }}>Hablante 0</span> = {speakerMap[0] || 'Agente'}
              <span className="mx-1">/</span>
              <span className="font-mono" style={{ color: 'var(--text-muted)' }}>Hablante 1</span> = {speakerMap[1] || 'Cliente'}
            </span>
          </div>
        )}

        {/* Twilio call banner */}
        {session && callStatus === 'in-progress' && (
          <div
            className="flex items-center justify-center gap-3 py-2 text-xs"
            style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: '#22c55e' }}
          >
            <PhoneCall size={12} className="pulse-dot" />
            <span className="font-semibold">Llamada Twilio en curso</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span className="font-mono">{callingNumber}</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ color: 'var(--text-muted)' }}>
              Transcripcion y analisis IA en tiempo real
            </span>
          </div>
        )}

        {session && callStatus === 'ringing' && (
          <div
            className="flex items-center justify-center gap-3 py-2 text-xs"
            style={{ backgroundColor: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}
          >
            <Phone size={12} className="pulse-dot" />
            <span className="font-semibold">Llamando a {callingNumber}...</span>
          </div>
        )}

        {/* Transcript Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="transcript-area">
          {!session && !showDialPad && (
            <div className="flex flex-col items-center justify-center h-full text-center" style={{ color: 'var(--text-muted)' }}>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <Phone size={32} style={{ color: 'var(--accent-primary)' }} />
              </div>
              <h2 className="font-heading text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Listo para tu Llamada
              </h2>
              <p className="text-sm max-w-md leading-relaxed">
                Inicia una sesión con micrófono o realiza una llamada telefónica con el marcador.
              </p>
              <div className="flex items-center gap-2 mt-4 text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <Zap size={12} style={{ color: 'var(--accent-primary)' }} />
                <span>Deepgram Nova-2 + GPT-4o + Qdrant</span>
              </div>
            </div>
          )}

          {!session && showDialPad && (
            <div className="flex items-center justify-center h-full">
              <div className="w-72 p-6 rounded-2xl border" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
                <DialPad
                  onCall={makeCall}
                  onClose={() => setShowDialPad(false)}
                  calling={callStatus === 'connecting'}
                  callerNumber={callerNumber}
                />
              </div>
            </div>
          )}

          {session && transcript.length === 0 && !interimText && (
            <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--text-muted)' }}>
              {isStreaming || twilioLiveSession ? (
                <>
                  <div className="relative mb-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(239,68,68,0.15)' }}>
                      <Mic size={28} className="text-red-400" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full pulse-dot" />
                  </div>
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {deepgramConnected ? 'Escuchando con Deepgram...' : 'Conectando a Deepgram...'}
                  </p>
                  <p className="text-xs mt-1">Habla para comenzar la transcripción automática</p>
                </>
              ) : (
                <>
                  <Mic size={24} className="mb-3" style={{ color: 'var(--accent-primary)' }} />
                  <p className="text-sm">Activa el micrófono para comenzar</p>
                  <p className="text-xs mt-1">O sube un archivo de audio</p>
                </>
              )}
            </div>
          )}

          <AnimatePresence>
            {transcript.map((entry, idx) => {
              const isAgent = entry.speaker === 'Agente';
              return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${isAgent ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center gap-2 mb-1 ${isAgent ? 'justify-end' : ''}`}>
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: isAgent ? 'var(--accent-primary)' : 'var(--surface-secondary)',
                      }}
                    >
                      {isAgent ? <User size={10} className="text-white" /> : <Users size={10} style={{ color: 'var(--text-muted)' }} />}
                    </div>
                    {/* Clickable speaker label for reassignment */}
                    <div className="relative">
                      <button
                        onClick={() => setEditingSpeakerIdx(editingSpeakerIdx === idx ? null : idx)}
                        className="text-[11px] font-mono font-medium cursor-pointer hover:underline transition-all"
                        style={{ color: isAgent ? 'var(--accent-primary)' : 'var(--text-muted)' }}
                        data-testid={`speaker-label-${idx}`}
                        title="Click para cambiar hablante"
                      >
                        {entry.speaker}
                      </button>
                      {editingSpeakerIdx === idx && (
                        <div className="absolute z-20 top-full left-0 mt-1 rounded-lg border shadow-lg overflow-hidden"
                          style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', minWidth: '120px' }}
                          data-testid={`speaker-dropdown-${idx}`}>
                          {AVAILABLE_SPEAKERS.filter(s => s !== entry.speaker).map(sp => (
                            <button
                              key={sp}
                              onClick={() => changeSpeakerForEntry(idx, sp)}
                              className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:opacity-80"
                              style={{ color: sp === 'Agente' ? 'var(--accent-primary)' : 'var(--text-secondary)' }}
                              data-testid={`assign-speaker-${sp}-${idx}`}
                            >
                              {sp}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {entry.confidence !== undefined && (
                      <span className="text-[9px] font-mono" style={{ color: 'rgba(148,163,184,0.4)' }}>
                        {Math.round((entry.confidence || 0) * 100)}%
                      </span>
                    )}
                  </div>
                  <div
                    className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      isAgent ? 'rounded-tr-sm border' : 'rounded-tl-sm'
                    }`}
                    style={{
                      backgroundColor: isAgent ? 'transparent' : 'var(--surface-secondary)',
                      color: isAgent ? 'var(--text-primary)' : 'var(--text-muted)',
                      borderColor: isAgent ? 'var(--border-color)' : 'transparent',
                    }}
                  >
                    {entry.text}
                  </div>
                  {entry.timestamp && (
                    <p className="text-[10px] mt-1 font-mono" style={{ color: 'rgba(148,163,184,0.5)' }}>
                      {new Date(entry.timestamp).toLocaleTimeString('es-ES')}
                    </p>
                  )}
                </div>
              </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Interim text */}
          {interimText && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              className={`flex ${interimSpeaker === 'Agente' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[85%] ${interimSpeaker === 'Agente' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center gap-2 mb-1 ${interimSpeaker === 'Agente' ? 'justify-end' : ''}`}>
                  <span className="text-[11px] font-mono" style={{ color: 'var(--text-muted)' }}>
                    {interimSpeaker || '...'}
                  </span>
                  <span className="text-[9px] italic" style={{ color: 'var(--text-muted)' }}>escuchando...</span>
                </div>
                <div
                  className="px-4 py-3 rounded-2xl text-sm leading-relaxed italic border-dashed border"
                  style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)', opacity: 0.5 }}
                  data-testid="interim-text"
                >
                  {interimText}
                </div>
              </div>
            </motion.div>
          )}

          <div ref={transcriptEndRef} />
        </div>
      </div>

      {/* RIGHT: Suggestions Panel (40%) */}
      <div className="w-full lg:w-[40%] flex flex-col" style={{ backgroundColor: 'rgba(30,33,48,0.5)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Lightbulb size={18} style={{ color: 'var(--accent-primary)' }} />
            <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Sugerencias IA</h3>
          </div>
          {newSuggestionCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold text-white flex items-center gap-1" style={{ backgroundColor: 'var(--accent-primary)' }} data-testid="new-suggestion-badge">
              <Bell size={10} />
              {newSuggestionCount}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="suggestions-panel">
          {suggestions.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4" style={{ color: 'var(--text-muted)' }}>
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--surface-secondary)' }}>
                <Lightbulb size={28} style={{ color: 'var(--accent-primary)' }} />
              </div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sin sugerencias aún</p>
              <p className="text-xs leading-relaxed">Las sugerencias aparecerán conforme la IA analice la conversación.</p>
            </div>
          )}

          <AnimatePresence>
            {suggestions.map((suggestion, idx) => {
              const config = SUGGESTION_CONFIG[suggestion.type] || SUGGESTION_CONFIG.consejo;
              const urgency = URGENCY_COLORS[suggestion.urgency] || URGENCY_COLORS.medium;
              const Icon = config.icon;
              return (
                <motion.div
                  key={suggestion.id || idx}
                  initial={{ x: 100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className={`${config.borderClass} rounded-r-lg p-4 shadow-lg transition-all duration-200 hover:translate-x-[-4px]`}
                  style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}
                  data-testid={`suggestion-card-${suggestion.type}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon size={16} style={{ color: config.color }} />
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: config.color }}>{config.label}</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ backgroundColor: urgency.bg, color: urgency.text }}>{suggestion.urgency}</span>
                  </div>
                  <h4 className="font-heading font-bold text-sm mb-1.5" style={{ color: 'var(--text-primary)' }}>{suggestion.title}</h4>
                  <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--text-secondary)' }}>{suggestion.body}</p>
                  {suggestion.product_ref && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-md" style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--accent-primary)' }}>
                      <ChevronRight size={10} />{suggestion.product_ref}
                    </span>
                  )}
                  {/* Feedback buttons */}
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <button
                      onClick={() => sendFeedback(suggestion.id, 'accepted')}
                      data-testid={`accept-suggestion-${suggestion.id}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${
                        suggestion.feedback === 'accepted' ? 'bg-green-500/20 text-green-400' : ''
                      }`}
                      style={suggestion.feedback !== 'accepted' ? { color: 'var(--text-muted)' } : {}}
                    >
                      <ThumbsUp size={10} />
                      Útil
                    </button>
                    <button
                      onClick={() => sendFeedback(suggestion.id, 'dismissed')}
                      data-testid={`dismiss-suggestion-${suggestion.id}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${
                        suggestion.feedback === 'dismissed' ? 'bg-red-500/20 text-red-400' : ''
                      }`}
                      style={suggestion.feedback !== 'dismissed' ? { color: 'var(--text-muted)' } : {}}
                    >
                      <ThumbsDown size={10} />
                      No útil
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <div ref={suggestionsEndRef} />
        </div>
      </div>
    </div>
  );
}
